/**
 * Centralized Equipment Status Definitions
 * Used across frontend and can be shared with backend
 */

import {
  CheckCircle2,
  Check,
  ClipboardList,
  Wrench,
  Archive,
  AlertTriangle,
  Package,
  Undo2
} from 'lucide-react';

// V2 Equipment status definitions (lowercase values for database compatibility)
export const EQUIPMENT_STATUS = {
  available: {
    label: 'Available',
    color: '#4caf50',
    icon: Check,
    description: 'Ready for assignment'
  },
  received: {
    label: 'Received',
    color: '#4caf50',
    icon: Package,
    description: 'Received in warehouse'
  },
  assigned: {
    label: 'Assigned',
    color: '#ff9800',
    icon: ClipboardList,
    description: 'Assigned to job (in warehouse)'
  },
  checked_out: {
    label: 'Checked Out',
    color: '#9c27b0',
    icon: CheckCircle2,
    description: 'With technician/in transit'
  },
  installed: {
    label: 'Installed',
    color: '#2196f3',
    icon: Wrench,
    description: 'Physically installed at job site'
  },
  returned: {
    label: 'Returned',
    color: '#4caf50',
    icon: Undo2,
    description: 'Returned from job'
  },
  maintenance: {
    label: 'Maintenance',
    color: '#f44336',
    icon: AlertTriangle,
    description: 'Under maintenance or repair'
  },
  archived: {
    label: 'Archived',
    color: '#607d8b',
    icon: Archive,
    description: 'Archived/retired equipment'
  }
};

// Legacy V1 status mapping for backward compatibility (DEPRECATED)
export const LEGACY_STATUS_MAP = {
  PLANNED: 'available',
  ORDERED: 'available', 
  RECEIVED: 'available',
  AVAILABLE: 'available',
  ASSIGNED: 'assigned',
  INSTALLED: 'assigned',
  RETURNED: 'available',
  MAINTENANCE: 'maintenance',
  IN_STOCK: 'available',
  OUT_OF_STOCK: 'archived'
};

// Get status display properties (V2 compatible with legacy fallback)
export const getStatusDisplay = (status) => {
  // Handle undefined/null status
  if (!status) {
    return {
      label: 'Unknown',
      color: '#757575',
      icon: Check,
      description: ''
    };
  }
  
  // Normalize to V2 status if it's a V1 status
  const normalizedStatus = LEGACY_STATUS_MAP[status] || (status && typeof status === 'string' ? status.toLowerCase() : 'available');
  
  const config = EQUIPMENT_STATUS[normalizedStatus] || {
    label: status,
    color: '#757575',
    icon: Check,
    description: ''
  };
  return config;
};

// Convert V1 status to V2 status
export const normalizeStatus = (status) => {
  if (!status) return 'available';
  return LEGACY_STATUS_MAP[status] || (status && typeof status === 'string' ? status.toLowerCase() : 'available');
};

// Check if status is V2 compliant
export const isV2Status = (status) => {
  return VALID_STATUSES.includes(status);
};

// Valid V2 status array for validation
export const VALID_STATUSES = Object.keys(EQUIPMENT_STATUS);

// Valid status transitions (must match backend equipmentStatusValidator.js)
export const VALID_TRANSITIONS = {
  'available': ['received', 'assigned', 'maintenance', 'archived'],
  'received': ['assigned', 'available', 'maintenance', 'archived'],
  'assigned': ['checked_out', 'returned', 'maintenance', 'archived', 'available'],  // Allow assigned → available for job cancellation
  'checked_out': ['installed', 'returned', 'available', 'maintenance', 'archived'],  // Tech has equipment, can return directly to warehouse
  'installed': ['checked_out', 'returned', 'available', 'maintenance', 'archived'],  // Can be uninstalled or returned directly to warehouse
  'returned': ['available', 'received', 'assigned', 'maintenance', 'archived'],  // Allow direct reassignment
  'maintenance': ['available', 'archived'],
  'archived': [] // Terminal state - no transitions allowed
};

// Check if a status transition is valid
export const isValidTransition = (currentStatus, newStatus) => {
  const current = currentStatus?.toLowerCase();
  const next = newStatus?.toLowerCase();

  // Same status is always allowed
  if (current === next) return true;

  // Check if transition is allowed
  const allowedTransitions = VALID_TRANSITIONS[current] || [];
  return allowedTransitions.includes(next);
};

// Get allowed next statuses for current status
export const getAllowedTransitions = (currentStatus) => {
  const current = currentStatus?.toLowerCase();
  return VALID_TRANSITIONS[current] || [];
};

/**
 * DEPRECATED: Use getStatusDisplay() with sx prop instead
 * This function is kept for backward compatibility but should not be used in new code
 *
 * Get MUI Chip sx styling for equipment status
 * Uses hex colors from EQUIPMENT_STATUS constants for accurate color display
 * @param {string} status - Equipment status (lowercase)
 * @param {string} variant - Chip variant ('filled' or 'outlined')
 * @returns {object} MUI sx prop object with backgroundColor and color
 */
export const getStatusChipSx = (status, variant = 'filled') => {
  const display = getStatusDisplay(status);

  if (variant === 'outlined') {
    return {
      borderColor: display.color,
      color: display.color,
      backgroundColor: 'transparent',
      fontWeight: 500,
      '&:hover': {
        backgroundColor: `${display.color}15`, // 15 = ~8% opacity in hex
      }
    };
  }

  return {
    backgroundColor: display.color,
    color: 'white',
    fontWeight: 500,
    '&:hover': {
      opacity: 0.9,
    }
  };
};

// V2 Status lifecycle order (complete flow)
export const STATUS_LIFECYCLE = [
  'available',
  'received',
  'assigned',
  'checked_out',  // Added - tech has equipment
  'installed',
  'returned',
  'maintenance',
  'archived'
];

// Get next logical status in lifecycle
export const getNextStatus = (currentStatus) => {
  const currentIndex = STATUS_LIFECYCLE.indexOf(currentStatus);
  if (currentIndex === -1 || currentIndex === STATUS_LIFECYCLE.length - 1) {
    return null;
  }
  return STATUS_LIFECYCLE[currentIndex + 1];
};

// Get previous status in lifecycle
export const getPreviousStatus = (currentStatus) => {
  const currentIndex = STATUS_LIFECYCLE.indexOf(currentStatus);
  if (currentIndex <= 0) {
    return null;
  }
  return STATUS_LIFECYCLE[currentIndex - 1];
};

/**
 * Get MUI Chip variant for equipment status
 * Returns 'outlined' for returned status to differentiate from available
 * @param {string} status - Equipment status (lowercase)
 * @returns {string} MUI Chip variant ('filled' or 'outlined')
 */
export const getStatusChipVariant = (status) => {
  const normalizedStatus = status?.toLowerCase();
  return normalizedStatus === 'returned' ? 'outlined' : 'filled';
};